
import React, { useRef, useEffect } from 'react';
import { Clock, AlertCircle, BarChart4, ArrowRight } from 'lucide-react';

const ProblemStatement = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    const elements = sectionRef.current.querySelectorAll('.reveal');
    elements.forEach(el => observer.observe(el));
    
    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <section
      id="problems"
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="heading-lg mb-6 reveal opacity-0 transition-all duration-700 translate-y-8">
            The Challenge in Alternative Investments
          </h2>
          <p className="text-gray-600 text-lg reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '100ms' }}>
            Private equity firms face significant challenges managing complex data from various sources, leading to inefficiencies and potential missed opportunities.
          </p>
        </div>
        
        {/* Moved challenge boxes directly under the heading */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
          <div className="feature-card reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '200ms' }}>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Time-consuming manual data entry</h3>
            <p className="text-gray-600">
              Analysts spend countless hours extracting and inputting data from quarterly reports, limiting high-value analysis.
            </p>
          </div>
          
          <div className="feature-card reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '300ms' }}>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">High risk of human error</h3>
            <p className="text-gray-600">
              Manual processing introduces mistakes in critical financial data, potentially leading to costly investment decisions.
            </p>
          </div>
          
          <div className="feature-card reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '400ms' }}>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <BarChart4 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Lack of real-time insights</h3>
            <p className="text-gray-600">
              Traditional reporting lacks immediate visibility into changing market conditions and portfolio performance.
            </p>
          </div>
        </div>
        
        <div className="relative mb-16">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent max-w-4xl mx-auto"></div>
          </div>
          
          <div className="relative flex flex-wrap justify-center items-center">
            <div className="pdf-animation w-full md:w-auto reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '500ms' }}>
              <div className="relative mx-auto w-64 h-80 bg-white rounded-lg shadow-lg overflow-hidden transform rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white"></div>
                <div className="absolute top-0 left-0 w-full h-10 bg-blue-600"></div>
                <div className="absolute top-14 left-6 w-3/4 h-2 bg-gray-300 rounded"></div>
                <div className="absolute top-20 left-6 w-1/2 h-2 bg-gray-300 rounded"></div>
                <div className="absolute top-32 left-6 w-5/6 h-2 bg-gray-300 rounded"></div>
                <div className="absolute top-38 left-6 w-4/6 h-2 bg-gray-300 rounded"></div>
                <div className="absolute top-44 left-6 w-3/6 h-2 bg-gray-300 rounded"></div>
                <div className="absolute bottom-24 left-6 w-5/6 h-24 bg-gray-200 rounded"></div>
                <div className="absolute bottom-6 right-6 text-xs text-gray-400">Quarterly Report.pdf</div>
              </div>
              
              <div className="mt-8 text-center font-medium">
                Manual Data Processing
              </div>
            </div>
            
            <div className="hidden md:flex items-center justify-center mx-8 w-24 h-24 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '550ms' }}>
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                <ArrowRight className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            
            <div className="w-full md:w-auto mt-12 md:mt-0 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '600ms' }}>
              <div className="relative mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-blue-100">
                <img 
                  src="/lovable-uploads/dashboard_portco.png" 
                  alt="Portfolio Insights Dashboard" 
                  className="w-full max-w-full md:max-w-[600px] lg:max-w-[700px]"
                />
                <img 
                  src="/lovable-uploads/fund_performance_overview.png" 
                  alt="Fund Performance Overview" 
                  className="w-full max-w-full md:max-w-[600px] lg:max-w-[700px] border-t border-gray-100"
                />
              </div>
              
              <div className="mt-8 text-center font-medium">
                Structured Insights
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemStatement;
